import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import {
  fetchRecentTransactions,
  fetchAccountData,
  fetchSorobanEvents,
  fetchOnChainScore,
  revokeEndorsement,
  updateEndorsement,
} from '../components/Freighter';
import * as StellarSdk from "@stellar/stellar-sdk";
const ScoreRing = ({ score, size = 200, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 1000) * circumference;

  return (
    <div className="score-ring-container" style={{ width: size, height: size }}>
      <svg className="score-ring-svg" width={size} height={size}>
        <defs>
          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00e5ff" />
            <stop offset="100%" stopColor="#00bfa5" />
          </linearGradient>
        </defs>
        <circle
          className="score-ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="score-ring-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="score-ring-value">
        <div className="score-ring-number">{score}</div>
        <div className="score-ring-label">Score Index</div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { publicKey, connected } = useWallet();
  const [transactions, setTransactions] = useState([]);
  const [accountData, setAccountData] = useState({});
  const [sorobanEvents, setSorobanEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reputationScore, setReputationScore] = useState(0);
  const [activeTab, setActiveTab] = useState('received'); // 'received' | 'given'
  const [editingItem, setEditingItem] = useState(null); // { target, category, review }
  const [editCategory, setEditCategory] = useState('');
  const [editReview, setEditReview] = useState('');
  const [actionMessage, setActionMessage] = useState(null);

  const handleRevoke = async (targetAddr) => {
    if (!publicKey) return;
    if (!window.confirm("Are you sure you want to revoke this on-chain endorsement? Points will be deducted from the target.")) return;
    setActionMessage({ type: 'info', text: 'Submitting revocation transaction to Soroban network...' });
    const res = await revokeEndorsement(publicKey, targetAddr);
    if (res.success) {
      setActionMessage({ type: 'success', text: `Endorsement revoked successfully! Hash: ${res.hash.slice(0, 8)}...` });
    } else {
      setActionMessage({ type: 'error', text: `Revocation failed: ${res.error}` });
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem || !publicKey) return;
    setActionMessage({ type: 'info', text: 'Submitting update transaction to Soroban network...' });
    const res = await updateEndorsement(publicKey, editingItem.target, editCategory, editReview);
    if (res.success) {
      setActionMessage({ type: 'success', text: `Endorsement updated on-chain! Hash: ${res.hash.slice(0, 8)}...` });
      setEditingItem(null);
    } else {
      setActionMessage({ type: 'error', text: `Update failed: ${res.error}` });
    }
  };

  useEffect(() => {
    if (!connected || !publicKey) return;

    const load = async () => {
      setLoading(true);
      try {
        const [txs, data, events, score] = await Promise.all([
          fetchRecentTransactions(publicKey),
          fetchAccountData(publicKey),
          fetchSorobanEvents(),
          fetchOnChainScore(publicKey)
        ]);
        setTransactions(txs);
        setAccountData(data);
        setSorobanEvents(events);
        setReputationScore(score);
      } catch (err) {
        console.error('[Dashboard] Load error:', err);
        setReputationScore(0);
      } finally {
        setLoading(false);
      }
    };
    load();
    
    // Poll for new events every 10 seconds (Real-time Event Integration)
    const intervalId = setInterval(async () => {
        const events = await fetchSorobanEvents();
        setSorobanEvents(events);
        
        // Also refresh score
        if (publicKey) {
          const score = await fetchOnChainScore(publicKey);
          setReputationScore(score);
        }
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, [connected, publicKey]);

  // Compute endorsement count from account data
  const endorsementKeys = useMemo(
    () => Object.keys(accountData).filter((k) => k.startsWith('repute:')),
    [accountData]
  );
  const endorsementCount = endorsementKeys.length;

  // Calculate rank percentage
  const rankPercent = useMemo(() => {
    if (reputationScore >= 900) return 'Top 1%';
    if (reputationScore >= 800) return 'Top 2%';
    if (reputationScore >= 700) return 'Top 4%';
    if (reputationScore >= 600) return 'Top 10%';
    if (reputationScore >= 400) return 'Top 25%';
    if (reputationScore >= 200) return 'Top 50%';
    return 'Bottom 50%';
  }, [reputationScore]);

  // Generate fake activity chart bars based on transaction volume
  const activityBars = useMemo(() => {
    const bars = new Array(24).fill(0).map(() => Math.floor(Math.random() * 20 + 5));
    if (!transactions.length) return bars;

    transactions.forEach((tx) => {
      const txDate = new Date(tx.createdAt);
      const now = new Date();
      const diffHours = Math.floor((now - txDate) / (1000 * 60 * 60));
      if (diffHours < 24) {
        const i = 23 - diffHours;
        bars[i] = Math.min(100, bars[i] + (tx.operationCount || 1) * 10);
      }
    });
    return bars;
  }, [transactions]);

  // Format activity items from real-time Soroban events
  const activityItems = useMemo(() => {
    if (sorobanEvents.length > 0) {
        return sorobanEvents.map((evt, i) => {
            let cat = "Endorsement";
            let addr = "Unknown";
            let desc = "Received endorsement";
            try {
                // Topic 1 is target address, Topic 2 is sender address
                const targetAddress = StellarSdk.scValToNative(StellarSdk.xdr.ScVal.fromXDR(evt.topic[1], "base64"));
                addr = `${targetAddress.slice(0, 5)}...${targetAddress.slice(-4)}`;
                
                // Value is a tuple: [category, points_added]
                const valArray = StellarSdk.scValToNative(StellarSdk.xdr.ScVal.fromXDR(evt.value, "base64"));
                if (Array.isArray(valArray)) {
                    cat = valArray[0];
                    const weight = valArray[1];
                    desc = `Received endorsement for "${cat}" (+${weight} pts)`;
                } else {
                    cat = valArray; // Fallback for older events
                    desc = `Received endorsement for "${cat}"`;
                }
            } catch(e) {}
            
            return {
                id: evt.id,
                address: addr,
                description: desc,
                dotClass: 'activity-dot-cyan',
                time: `Ledger ${evt.ledger}`,
            };
        });
    }

    return transactions.slice(0, 5).map((tx, i) => {
      const dots = ['activity-dot-cyan', 'activity-dot-green', 'activity-dot-purple'];
      const shortAddr = tx.sourceAccount ? `${tx.sourceAccount.slice(0, 5)}...${tx.sourceAccount.slice(-4)}` : 'Unknown';

      // Bug 3 fix: Soroban invokeHostFunction transactions do NOT have traditional memos.
      // The memo field is always empty for contract calls. Instead of trying to parse
      // a non-existent memo, we identify these as contract interactions directly.
      let desc;
      if (tx.memo && typeof tx.memo === 'string' && tx.memo.length > 0) {
        // If there IS a memo (non-contract tx), parse it
        console.log(`[Activity] TX ${tx.hash.slice(0,8)} has memo: "${tx.memo}"`);
        if (tx.memo.startsWith('endorse:')) {
          const target = tx.memo.split(':')[1];
          const targetShort = target && target.length > 8 ? `${target.slice(0, 5)}...${target.slice(-4)}` : target;
          desc = `Endorsed ${targetShort}`;
        } else {
          desc = tx.memo;
        }
      } else {
        // Soroban contract calls — no memo exists
        // All transactions to this wallet are reputation actions
        desc = `Endorsed ${shortAddr} via smart contract`;
      }

      return {
        id: tx.hash,
        address: shortAddr,
        description: desc,
        dotClass: dots[i % dots.length],
        time: new Date(tx.createdAt).toLocaleDateString(),
      };
    });
  }, [transactions, sorobanEvents]);

  const displayActivity = activityItems;

  if (!connected) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <h2 style={{ color: 'var(--text-primary)', marginBottom: 16 }}>
          Connect your wallet to view your dashboard
        </h2>
        <Link to="/" className="btn btn-cyan">
          Go to Landing Page
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-in">
      {actionMessage && (
        <div
          className="animate-in"
          style={{
            background: actionMessage.type === 'success' ? 'rgba(0, 230, 118, 0.1)' : actionMessage.type === 'error' ? 'rgba(255, 23, 68, 0.1)' : 'rgba(0, 229, 255, 0.1)',
            border: `1px solid ${actionMessage.type === 'success' ? '#00e676' : actionMessage.type === 'error' ? '#ff1744' : 'var(--cyan)'}`,
            color: actionMessage.type === 'success' ? '#00e676' : actionMessage.type === 'error' ? '#ff1744' : 'var(--cyan)',
            padding: '12px 18px',
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 13,
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{actionMessage.text}</span>
          <button
            onClick={() => setActionMessage(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 700 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Quick Action Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        <Link to="/endorse" className="quick-card" style={{ textDecoration: 'none' }}>
          <div className="quick-card-icon">🤝</div>
          <div className="quick-card-title">Endorse a Wallet</div>
          <div className="quick-card-subtitle">New Attestation</div>
        </Link>
        <Link to="/lookup" className="quick-card" style={{ textDecoration: 'none' }}>
          <div className="quick-card-icon">🔍</div>
          <div className="quick-card-title">Look Up Reputation</div>
          <div className="quick-card-subtitle">Query Ledger</div>
        </Link>
      </div>

      <div className="dash-grid">
        {/* Left Column: Identity + Score */}
        <div>
          {/* Identity Card */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div className="section-label">Identity Anchor</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--text-primary)' }}>
                  {publicKey ? `${publicKey.slice(0, 6)}...${publicKey.slice(-4)}` : 'N/A'}
                  <button
                    onClick={() => navigator.clipboard.writeText(publicKey)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      marginLeft: 8,
                      fontSize: 12,
                    }}
                    title="Copy full address"
                  >
                    📋
                  </button>
                </div>
              </div>
              <span className="badge badge-verified">✓ Verified</span>
            </div>
          </div>

          {/* Score Ring */}
          <div className="card dash-score-section" style={{ alignItems: 'center', textAlign: 'center' }}>
            {loading ? (
              <div style={{ padding: 60 }}>
                <div className="spinner" style={{ width: 40, height: 40 }} />
              </div>
            ) : (
              <>
                <ScoreRing score={reputationScore} size={200} />
                <div className="dash-stats-row">
                  <div>
                    <div className="section-label">Endorsements</div>
                    <div className="dash-stat-value text-cyan">
                      {endorsementCount}
                    </div>
                    <div className="dash-stat-label">Peers</div>
                  </div>
                  <div>
                    <div className="section-label">Rank</div>
                    <div className="dash-stat-value">{rankPercent}</div>
                    <div className="dash-stat-label">Network</div>
                  </div>
                </div>
                <Link to="/lookup" className="link-arrow">
                  View Full Ledger History →
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Right Column: Activity + Momentum */}
        <div>
          {/* Recent Activity */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                borderBottom: '1px solid var(--border-default)',
                paddingBottom: 12,
              }}
            >
              <div style={{ display: 'flex', gap: 16 }}>
                <button
                  onClick={() => setActiveTab('received')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: activeTab === 'received' ? 'var(--cyan)' : 'var(--text-secondary)',
                    fontWeight: activeTab === 'received' ? 600 : 400,
                    fontSize: 14,
                    cursor: 'pointer',
                    padding: 0,
                    borderBottom: activeTab === 'received' ? '2px solid var(--cyan)' : 'none',
                    paddingBottom: 4,
                  }}
                >
                  Recent Activity
                </button>
                <button
                  onClick={() => setActiveTab('given')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: activeTab === 'given' ? 'var(--cyan)' : 'var(--text-secondary)',
                    fontWeight: activeTab === 'given' ? 600 : 400,
                    fontSize: 14,
                    cursor: 'pointer',
                    padding: 0,
                    borderBottom: activeTab === 'given' ? '2px solid var(--cyan)' : 'none',
                    paddingBottom: 4,
                  }}
                >
                  Outbox (Given Endorsements)
                </button>
              </div>
              <span className="link-arrow" style={{ fontSize: 11 }}>
                Export List
              </span>
            </div>

            {displayActivity.map((item) => (
              <div className="activity-item" key={item.id}>
                <div className={`activity-dot ${item.dotClass}`} />
                <div className="activity-content">
                  <div className="activity-address">{item.address}</div>
                  <div className="activity-description">{item.description}</div>
                </div>
                <div className="activity-time">{item.time}</div>
              </div>
            ))}
          </div>

          {/* Score Momentum */}
          <div className="card">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>Score Momentum</div>
              <span className="text-cyan" style={{ fontSize: 13, fontWeight: 600 }}>
                +{(reputationScore * 0.065).toFixed(1)}%
              </span>
            </div>
            <div className="momentum-chart">
              {activityBars.map((h, i) => (
                <div
                  key={i}
                  className={`momentum-bar ${i === activityBars.length - 1 ? 'highlight' : i >= activityBars.length - 3 ? 'semi' : ''}`}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
